import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function QuoteForm() {
  const { t } = useTranslation('refection');
  const { ref, isVisible } = useScrollReveal<HTMLElement>(0.1);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    fetch(form.action, {
      method: form.method,
      body: data,
      headers: { Accept: 'application/json' },
    }).finally(() => {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/merci');
      }
    });
  };

  return (
    <section ref={ref} id="quote-form" className="bg-white py-16 md:py-24">
      <div className="max-w-2xl mx-auto px-4 md:px-6 lg:px-10">
        <div
          className={`text-center mb-10 md:mb-12 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl mb-3">
            {t('form_h2')}
          </h2>
          <p className="text-slate-primary/60 text-base md:text-lg">
            {t('form_subtitle')}
          </p>
        </div>

        <form
          data-readdy-form
          onSubmit={handleSubmit}
          action="https://readdy.ai/api/form/d84biee6qilughrrc820"
          method="POST"
          className={`bg-soft-gray rounded-xl p-6 md:p-10 space-y-5 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-slate-primary font-medium text-sm mb-2">
                {t('form_name_label')}
              </label>
              <input
                type="text"
                name="name"
                autoComplete="name"
                placeholder={t('form_name_placeholder')}
                required
                className="w-full px-4 py-3 rounded-lg border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
              />
            </div>
            <div>
              <label className="block text-slate-primary font-medium text-sm mb-2">
                {t('form_phone_label')}
              </label>
              <input
                type="tel"
                name="phone"
                inputMode="tel"
                autoComplete="tel"
                placeholder={t('form_phone_placeholder')}
                required
                className="w-full px-4 py-3 rounded-lg border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-primary font-medium text-sm mb-2">
              {t('form_email_label')}
            </label>
            <input
              type="email"
              name="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t('form_email_placeholder')}
              required
              className="w-full px-4 py-3 rounded-lg border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
            />
          </div>

          <div>
            <label className="block text-slate-primary font-medium text-sm mb-2">
              {t('form_service_label')}
            </label>
            <select
              name="service"
              required
              className="w-full px-4 py-3 rounded-lg border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors appearance-none"
            >
              <option value="">{t('form_service_select')}</option>
              <option value="refection">Réfection complète</option>
              <option value="reparation">Réparation / Infiltration</option>
              <option value="tole">Toiture tôle / métal</option>
              <option value="membrane">Membrane élastomère</option>
              <option value="inspection">Inspection</option>
              <option value="deneigement">Déneigement</option>
            </select>
          </div>

          <div>
            <label className="block text-slate-primary font-medium text-sm mb-2">
              {t('form_message_label')}
            </label>
            <textarea
              name="message"
              placeholder={t('form_message_placeholder')}
              rows={4}
              maxLength={500}
              onChange={(e) => setCharCount(e.target.value.length)}
              className="w-full px-4 py-3 rounded-lg border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors resize-none"
            />
            <p className="text-right text-xs text-slate-primary/40 mt-1">
              {charCount}{t('form_char_count')}
            </p>
          </div>

          <input
            type="text"
            name="_gotcha"
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          <button
            type="submit"
            className="w-full bg-copper hover:bg-copper-dark text-white font-semibold py-3.5 rounded-md transition-all duration-300 text-base whitespace-nowrap"
          >
            {t('cta_button')}
            <i className="ri-arrow-right-line ml-2" />
          </button>
        </form>
      </div>
    </section>
  );
}